// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useStepperContextStep } from "@store/Framework/Components/StepperContext";
import cancelDraftFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/CancelDraftFieldValues";
import saveDraftFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/SaveDraftFieldValues";
import setDraftFieldValues from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/Handlers/SetDraftFieldValues";
import { SimpleQuoteFormFieldValues } from "@store/Framework/Store/Greif/Components/SimpleQuoteForm/SimpleQuoteFormState";
import ProductTypeFormField, {
    ProductTypeFormFieldPresentationProps,
    ProductTypeFormFieldProps,
} from "@store/Library/Components/FormField/ProductTypeFormField";
import ReferenceProductIdFormField, {
    ReferenceProductIdFormFieldPresentationProps,
} from "@store/Library/Components/FormField/ReferenceProductIdFormField";
import FormStep, { FormStepPresentationProps, FormStepProps } from "@store/Library/Components/FormStepper/FormStep";
import ReferenceProduct from "@store/Library/Components/ReferenceProduct";
import { formStepProps } from "@store/Themes/common/props/formStep";
import React, { FC, useRef, useState } from "react";

const stepFieldValueKeys: Array<keyof SimpleQuoteFormFieldValues> = ["productType", "referenceProductId"];

export interface SimpleQuoteFormProductTypeStepProps extends Partial<Omit<FormStepProps, "contextStepNumber">> {
    productTypeOptions?: ProductTypeFormFieldProps["options"];
}

export interface SimpleQuoteFormProductTypeStepStyles {
    formStep?: FormStepPresentationProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    productTypeFormField?: ProductTypeFormFieldPresentationProps;
    referenceProductIdFormField?: ReferenceProductIdFormFieldPresentationProps;
}

export const simpleQuoteFormProductTypeStepStyles: SimpleQuoteFormProductTypeStepStyles = {
    infoGridContainer: formStepProps.infoGridContainer,
    infoGridItem: formStepProps.infoGridItem,
    infoLabel: formStepProps.infoLabel,
    infoValue: formStepProps.infoValue,
};

const styles = simpleQuoteFormProductTypeStepStyles;

const SimpleQuoteFormProductTypeStep: FC<SimpleQuoteFormProductTypeStepProps> = ({
    productTypeOptions,
    ...formStepProps
}) => {
    const dispatch = useAppDispatch();
    const [showErrors, setShowErrors] = useState(false);
    const { isComplete, stepNumber } = useStepperContextStep();
    const draftFieldValues = useAppSelector(state => state.greif.components.simpleQuoteForm.formDraftFieldValues);
    const fieldValues = useAppSelector(state => state.greif.components.simpleQuoteForm.formFieldValues);
    const referenceProductIsChecked = useRef(true);

    if (!stepNumber) {
        return null;
    }

    return (
        <FormStep
            {...styles.formStep}
            contextStepNumber={stepNumber}
            onCancel={() => dispatch(cancelDraftFieldValues(stepFieldValueKeys))}
            onSubmit={event => {
                if (!showErrors) {
                    setShowErrors(true);
                }

                if (stepFieldValueKeys.some(x => !draftFieldValues[x].isValid)) {
                    event.preventDefault();
                    return;
                }

                dispatch(saveDraftFieldValues(stepFieldValueKeys));
            }}
            title={translate("Product Type")}
            titleValue={isComplete ? fieldValues.productType.value || translate("None") : undefined}
            {...formStepProps}
        >
            {isComplete ? (
                fieldValues.referenceProductId.value ? (
                    <GridContainer {...styles.infoGridContainer}>
                        <GridItem {...styles.infoGridItem} width={12}>
                            <Typography {...styles.infoLabel}>{translate("Included Reference Product")}</Typography>
                            <ReferenceProduct productId={fieldValues.referenceProductId.value} />
                        </GridItem>
                    </GridContainer>
                ) : null
            ) : (
                <>
                    <ProductTypeFormField
                        {...styles.productTypeFormField}
                        defaultValue={draftFieldValues.productType.value}
                        onUpdate={productType => dispatch(setDraftFieldValues({ productType }))}
                        options={productTypeOptions}
                        showError={showErrors}
                    />
                    <ReferenceProductIdFormField
                        {...styles.referenceProductIdFormField}
                        defaultChecked={referenceProductIsChecked.current}
                        defaultValue={draftFieldValues.referenceProductId.value}
                        onChange={(event, checked) => {
                            referenceProductIsChecked.current = checked;
                        }}
                        onUpdate={referenceProductId => dispatch(setDraftFieldValues({ referenceProductId }))}
                    />
                </>
            )}
        </FormStep>
    );
};

export default SimpleQuoteFormProductTypeStep;
